import type {
  SystemType, PendulumParams, PhaseSpaceDimension, SimulationConfig,
} from './types.ts';
import {
  DEFAULT_PARAMS, DEFAULT_INITIAL_STATE, DEFAULT_SIM, DIM_ORDER, systemDimensions,
} from './types.ts';

const STORAGE_KEY = 'orbitOptimizer.library.v1';

const VALID_SYSTEMS: SystemType[] = [
  'rigid', 'elastic12', 'nonlinear', 'sculpture', 'resonantPendulum',
];

export const SYSTEM_BADGE: Record<SystemType, string> = {
  rigid: 'Rigid',
  elastic12: 'Elastic',
  nonlinear: 'Nonlinear',
  sculpture: 'Sculpture',
  resonantPendulum: 'Resonant',
};

export interface SavedPendulum {
  id: string;
  name: string;
  system: SystemType;
  params: PendulumParams;
  initialState: Record<PhaseSpaceDimension, number>;
  sim: { dt: number; stepsPerFrame: number };
  savedAt: number;
}

export interface ParsedDescription {
  system: SystemType;
  params: PendulumParams;
  /** null when the source had no initial state (e.g. an unsampled Stupendulous export). */
  initialState: Record<PhaseSpaceDimension, number> | null;
}

function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && isFinite(v);
}

/**
 * Parse a Stupendulous pendulum-description JSON into a (system, params,
 * initialState) triple. Unknown / out-of-range fields are ignored; missing param
 * fields fall back to the defaults, so a partial description still loads.
 * initialState is null when absent in the source.
 */
export function parseDescription(text: string): ParsedDescription {
  let obj: any;
  try {
    obj = JSON.parse(text);
  } catch (e) {
    throw new Error('Invalid JSON: ' + (e as Error).message);
  }
  if (!obj || typeof obj !== 'object') throw new Error('Expected a JSON object.');
  const system = obj.system as SystemType;
  if (!VALID_SYSTEMS.includes(system)) {
    throw new Error(`Unknown system "${obj.system}".`);
  }

  const params: PendulumParams = { ...DEFAULT_PARAMS };
  const srcParams = (obj.params && typeof obj.params === 'object') ? obj.params : {};
  for (const key of Object.keys(DEFAULT_PARAMS) as (keyof PendulumParams)[]) {
    if (isFiniteNumber(srcParams[key])) params[key] = srcParams[key];
  }
  params.sculptureN = Math.max(1, Math.min(4, Math.round(params.sculptureN)));

  let initialState: Record<PhaseSpaceDimension, number> | null = null;
  if (obj.initialState && typeof obj.initialState === 'object') {
    initialState = { ...DEFAULT_INITIAL_STATE };
    for (const dim of DIM_ORDER) {
      if (isFiniteNumber(obj.initialState[dim])) initialState[dim] = obj.initialState[dim];
    }
  }

  return { system, params, initialState };
}

/**
 * Serialise the current pendulum in the Stupendulous description format: only the
 * system-specific params and the active phase-space dimensions are emitted.
 */
export function describePendulum(config: SimulationConfig): string {
  const p = config.params;
  const params: Record<string, number> = {};
  if (config.system === 'sculpture') {
    params.sculptureWeight = p.sculptureWeight;
    params.sculptureRod = p.sculptureRod;
    params.sculptureAxle = p.sculptureAxle;
    params.sculptureReduction = p.sculptureReduction;
    params.sculptureN = Math.max(1, Math.min(4, Math.round(p.sculptureN)));
  } else if (config.system === 'resonantPendulum') {
    params.rpM0 = p.rpM0; params.rpM1 = p.rpM1;
    params.rpL0 = p.rpL0; params.rpL1 = p.rpL1; params.rpA0 = p.rpA0;
  } else {
    params.m1 = p.m1; params.m2 = p.m2; params.L1 = p.L1; params.L2 = p.L2;
    if (config.system === 'elastic12' || config.system === 'nonlinear') {
      params.k1 = p.k1; params.k2 = p.k2;
    }
  }

  const dims = systemDimensions(config.system, p.sculptureN);
  const initialState: Record<string, number> = {};
  for (const d of dims) initialState[d] = config.initialState[d];

  return JSON.stringify({ system: config.system, params, initialState }, null, 2);
}

function isValidSave(x: any): x is SavedPendulum {
  return !!x && typeof x === 'object'
    && typeof x.id === 'string' && typeof x.name === 'string'
    && VALID_SYSTEMS.includes(x.system);
}

export function listSaves(): SavedPendulum[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr.filter(isValidSave).sort((a, b) => b.savedAt - a.savedAt);
  } catch {
    return [];
  }
}

function persist(list: SavedPendulum[]): boolean {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    return true;
  } catch {
    return false;
  }
}

function newId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return Date.now().toString(36) + Math.random().toString(36).slice(2);
}

export function savePendulum(name: string, config: SimulationConfig): SavedPendulum | null {
  const list = listSaves();
  const entry: SavedPendulum = {
    id: newId(),
    name: name.trim() || 'Untitled',
    system: config.system,
    params: { ...config.params },
    initialState: { ...config.initialState },
    sim: { ...config.sim },
    savedAt: Date.now(),
  };
  list.unshift(entry);
  if (!persist(list)) return null;
  return entry;
}

export function deleteSave(id: string): void {
  persist(listSaves().filter((s) => s.id !== id));
}

export function defaultSaveName(config: SimulationConfig): string {
  const t = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${SYSTEM_BADGE[config.system]} ${pad(t.getHours())}:${pad(t.getMinutes())}:${pad(t.getSeconds())}`;
}

export { DEFAULT_SIM };
